
import React, { useRef, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertTriangle, CheckCircle2, FileUp, Loader2 } from "lucide-react";
import Papa from "papaparse";
import { getTableColumns } from "@/utils/csvUtils";
import { useToast } from "@/hooks/use-toast";

type CSVImportDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onImport: (rows: any[]) => void;
  importType: "hardware" | "cultivar" | "user_profiles";
};

export default function CSVImportDialog({
  open,
  onOpenChange,
  onImport,
  importType,
}: CSVImportDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [parsedRows, setParsedRows] = useState<any[]>([]);
  const [parseErrors, setParseErrors] = useState<any[]>([]);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [tableColumns, setTableColumns] = useState<{column_name: string; data_type: string}[]>([]);
  const [isLoadingSchema, setIsLoadingSchema] = useState(false);
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const { toast } = useToast();

  // Get table name based on importType
  const getTableName = () => {
    switch (importType) {
      case "hardware": return "hardware";
      case "cultivar": return "cultivars";
      default: return "";
    }
  };

  // Load table schema when dialog opens
  useEffect(() => {
    if (open) {
      const fetchTableSchema = async () => {
        setIsLoadingSchema(true);
        setSchemaError(null);
        try {
          const tableName = getTableName();
          console.log(`Fetching schema for ${tableName}`);
          const columns = await getTableColumns(tableName);
          
          if (!columns || columns.length === 0) {
            throw new Error(`No columns returned for table ${tableName}`);
          }
          
          console.log(`Retrieved ${columns.length} columns for ${tableName}`, columns);
          setTableColumns(columns);
        } catch (error) {
          console.error("Error loading table schema:", error);
          setSchemaError(error instanceof Error ? error.message : "Unknown error occurred");
          toast({
            title: "Error loading table schema",
            description: error instanceof Error ? error.message : "Unknown error",
            variant: "destructive"
          });
        } finally {
          setIsLoadingSchema(false);
        }
      };

      fetchTableSchema();
    } else {
      // Reset state when dialog closes
      setTableColumns([]);
      setParsedRows([]);
      setParseErrors([]);
      setFile(null);
      setIsPreviewVisible(false);
      setSchemaError(null);
    }
  }, [open, importType, toast]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setParsedRows([]);
    setParseErrors([]);
    
    if (f) {
      console.log(`Parsing file: ${f.name}`);
      
      Papa.parse(f, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          console.log(`Parsed ${results.data.length} rows from CSV`);
          setParsedRows(results.data as any[]);
          setParseErrors(results.errors);
          if (results.data.length > 0) {
            setIsPreviewVisible(true);
          }
        },
        error: (error) => {
          console.error("CSV parsing error:", error);
          setParseErrors([{ message: error.message }]);
          toast({
            title: "Error parsing CSV",
            description: error.message,
            variant: "destructive"
          });
        }
      });
    }
  };

  const handleImport = () => {
    if (parsedRows.length > 0) {
      console.log(`Importing ${parsedRows.length} rows`);
      onImport(parsedRows);
      toast({
        title: "Import started",
        description: `Processing ${parsedRows.length} rows of data`
      });
      setFile(null);
      setParsedRows([]);
      setParseErrors([]);
      setIsPreviewVisible(false);
      onOpenChange(false);
    } else {
      toast({
        title: "No data to import",
        description: "Please select a valid CSV file with data",
        variant: "destructive"
      });
    }
  };

  const handleRetrySchemaLoad = async () => {
    const tableName = getTableName();
    setIsLoadingSchema(true);
    setSchemaError(null);
    
    try {
      console.log(`Retrying schema load for ${tableName}`);
      const columns = await getTableColumns(tableName);
      console.log(`Retrieved ${columns.length} columns for ${tableName}`, columns);
      setTableColumns(columns);
      toast({
        title: "Schema loaded successfully",
        description: `Retrieved ${columns.length} columns for ${tableName}`
      });
    } catch (error) {
      console.error("Error reloading table schema:", error);
      setSchemaError(error instanceof Error ? error.message : "Unknown error occurred");
      toast({
        title: "Error loading table schema",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setIsLoadingSchema(false);
    }
  };

  const handleDialogClose = () => {
    setFile(null);
    setParsedRows([]);
    setParseErrors([]);
    setIsPreviewVisible(false);
    onOpenChange(false);
  };

  const getImportTypeName = () => {
    switch (importType) {
      case "hardware": return "Hardware";
      case "cultivar": return "Cultivars";
      default: return "Data";
    }
  };

  const renderPreviewTable = () => {
    if (!parsedRows.length) return null;
    
    // Get headers from first row
    const headers = Object.keys(parsedRows[0]).slice(0, 5); // Show only first 5 columns
    const hasMoreColumns = Object.keys(parsedRows[0]).length > 5;
    
    return (
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium">Preview (first 5 rows{hasMoreColumns ? ", first 5 columns" : ""})</h3>
          
          {tableColumns.length > 0 && (
            <div className="text-xs text-muted-foreground">
              <span className="font-medium">{tableColumns.length}</span> columns in database
            </div>
          )}
        </div>
        
        <div className="border rounded">
          <ScrollArea className="h-[200px]">
            <Table>
              <TableHeader>
                <TableRow>
                  {headers.map(header => (
                    <TableHead key={header} className="text-xs">
                      {header}
                      {tableColumns.find(col => col.column_name === header) && (
                        <Badge variant="outline" className="ml-1 text-[10px]">
                          {tableColumns.find(col => col.column_name === header)?.data_type.split('(')[0]}
                        </Badge>
                      )}
                    </TableHead>
                  ))}
                  {hasMoreColumns && (
                    <TableHead className="text-xs text-muted-foreground">+ more columns</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {parsedRows.slice(0, 5).map((row, index) => (
                  <TableRow key={index}>
                    {headers.map(header => (
                      <TableCell key={`${index}-${header}`} className="text-xs py-2">
                        {String(row[header] || '')}
                      </TableCell>
                    ))}
                    {hasMoreColumns && (
                      <TableCell className="text-xs text-muted-foreground py-2">...</TableCell>
                    )}
                  </TableRow>
                ))}
                {parsedRows.length > 5 && (
                  <TableRow>
                    <TableCell colSpan={headers.length + (hasMoreColumns ? 1 : 0)} className="text-xs text-center text-muted-foreground">
                      + {parsedRows.length - 5} more rows
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>
      </div>
    );
  };

  // Check for missing columns that are in the database but not in the CSV
  const getMissingColumns = () => {
    if (!tableColumns.length || !parsedRows.length) return [];
    
    const csvColumns = Object.keys(parsedRows[0]);
    return tableColumns
      .filter(col => !['id', 'created_at', 'updated_at'].includes(col.column_name)) // Exclude system columns
      .filter(col => !csvColumns.includes(col.column_name))
      .map(col => col.column_name);
  };

  const missingColumns = getMissingColumns();

  return (
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Import {getImportTypeName()} from CSV
          </DialogTitle>
          <DialogDescription>
            Upload a CSV file to import {getImportTypeName().toLowerCase()} data. 
            The file should have headers matching the required fields.
          </DialogDescription>
        </DialogHeader>
        <div>
          {isLoadingSchema ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              <span>Loading schema information...</span>
            </div>
          ) : schemaError ? (
            <div className="p-4 border border-destructive rounded mb-4">
              <div className="flex items-center gap-2 mb-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                <h3 className="font-medium">Schema Error</h3>
              </div>
              <p className="text-sm mb-3">{schemaError}</p>
              <Button 
                onClick={handleRetrySchemaLoad} 
                variant="outline" 
                className="w-full"
              >
                Retry Loading Schema
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Input 
                type="file"
                accept=".csv"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="flex-1"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                type="button"
              >
                <FileUp className="mr-2 h-4 w-4" />
                Select File
              </Button>
            </div>
          )}
          
          {file && (
            <div className="mt-2 text-sm flex items-center gap-2">
              <span className="text-muted-foreground">Selected:</span> 
              <span className="font-medium">{file.name}</span>
              <Badge variant="outline">{parsedRows.length} rows</Badge>
              {parseErrors.length > 0 && (
                <Badge variant="destructive" className="ml-2 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {parseErrors.length} errors
                </Badge>
              )}
            </div>
          )}
          
          {isPreviewVisible && renderPreviewTable()}
          
          {parseErrors.length > 0 && (
            <div className="mt-4 p-3 bg-destructive/10 border border-destructive/30 rounded text-sm text-destructive">
              <p className="font-medium flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" />
                CSV parsing errors detected:
              </p>
              <ul className="list-disc list-inside mt-1">
                {parseErrors.slice(0, 3).map((error, index) => (
                  <li key={index}>
                    {error.message} {error.row !== undefined && `(row ${error.row})`}
                  </li>
                ))}
                {parseErrors.length > 3 && (
                  <li>...and {parseErrors.length - 3} more errors</li>
                )}
              </ul>
            </div>
          )}
          
          {missingColumns.length > 0 && (
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/30 rounded text-sm text-amber-700 dark:text-amber-400">
              <p className="font-medium flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" />
                Missing columns in CSV:
              </p>
              <p className="text-xs mt-1">The following columns exist in the database but are not in your CSV file. They will use default values:</p>
              <div className="mt-2 flex flex-wrap gap-1">
                {missingColumns.map(col => (
                  <Badge key={col} variant="outline" className="bg-amber-100/50 dark:bg-amber-900/30">
                    {col}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          {parsedRows.length > 0 && parseErrors.length === 0 && (
            <div className="mt-4 p-3 bg-success/10 border border-success/30 rounded text-sm text-success">
              <p className="font-medium flex items-center gap-1">
                <CheckCircle2 className="h-4 w-4" />
                CSV parsed successfully with {parsedRows.length} rows
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={handleDialogClose}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            disabled={!parsedRows.length || !!schemaError}
            onClick={handleImport}
          >
            Import {parsedRows.length} Rows
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
